import {
  CalendarCheck,
  CalendarPlus,
  History,
  Inbox,
  MapPin,
  MoreHorizontal,
  RotateCcw,
  Users,
} from 'lucide-react';
import {
  Button,
  Card,
  Chip,
  Description,
  Dropdown,
  EmptyState,
  Heading,
  Table,
} from '@heroui/react';
import { hasFloorPlanLocation } from '../../../components/floor-plan/floor-plan-utils';
import type { ParkingSpotStatus } from '../../../api/condominiums';
import type { ParkingSpotOwner, SpotUsage } from '../../../api/my-parking-spots';
import { SpotStatusChip } from './spot-status-chip';

export interface OwnedSpotRow {
  spot: ParkingSpotOwner;
  status: ParkingSpotStatus;
  pendingCount: number;
  activeWindowsCount: number;
  activeUsage: SpotUsage | null;
}

interface OwnedSpotsSectionProps {
  rows: OwnedSpotRow[];
  onAvailability: (spot: ParkingSpotOwner) => void;
  onActiveWindows: (spot: ParkingSpotOwner) => void;
  onPending: (spot: ParkingSpotOwner) => void;
  onUsages: (spot: ParkingSpotOwner) => void;
  onViewLocation: (spot: ParkingSpotOwner) => void;
  onReclaim: (usageId: string) => void;
}

function SpotActionsMenu({
  row,
  onAvailability,
  onActiveWindows,
  onPending,
  onUsages,
  onViewLocation,
  onReclaim,
}: {
  row: OwnedSpotRow;
  onAvailability: (spot: ParkingSpotOwner) => void;
  onActiveWindows: (spot: ParkingSpotOwner) => void;
  onPending: (spot: ParkingSpotOwner) => void;
  onUsages: (spot: ParkingSpotOwner) => void;
  onViewLocation: (spot: ParkingSpotOwner) => void;
  onReclaim: (usageId: string) => void;
}) {
  const canViewLocation = hasFloorPlanLocation(row.spot);

  return (
    <Dropdown>
      <Dropdown.Trigger aria-label={`Ações da vaga ${row.spot.spotCode}`}>
        <Button isIconOnly size="sm" variant="ghost">
          <MoreHorizontal size={16} />
        </Button>
      </Dropdown.Trigger>
      <Dropdown.Popover placement="bottom end">
        <Dropdown.Menu
          onAction={(key) => {
            if (key === 'location') onViewLocation(row.spot);
            if (key === 'availability') onAvailability(row.spot);
            if (key === 'active-windows') onActiveWindows(row.spot);
            if (key === 'pending') onPending(row.spot);
            if (key === 'usages') onUsages(row.spot);
            if (key === 'reclaim' && row.activeUsage) onReclaim(row.activeUsage.id);
          }}
        >
          {row.activeUsage ? (
            <Dropdown.Item id="reclaim" textValue="Retomar vaga">
              <RotateCcw size={16} className="mr-2 inline" />
              Retomar vaga
            </Dropdown.Item>
          ) : null}
          <Dropdown.Item id="availability" textValue="Disponibilizar">
            <CalendarPlus size={16} className="mr-2 inline" />
            Disponibilizar
          </Dropdown.Item>
          {row.activeWindowsCount > 0 ? (
            <Dropdown.Item id="active-windows" textValue="Janelas ativas">
              <CalendarCheck size={16} className="mr-2 inline" />
              Janelas ativas
            </Dropdown.Item>
          ) : null}
          {canViewLocation ? (
            <Dropdown.Item id="location" textValue="Ver no mapa">
              <MapPin size={16} className="mr-2 inline" />
              Ver no mapa
            </Dropdown.Item>
          ) : null}
          <Dropdown.Item id="pending" textValue="Solicitações">
            <Users size={16} className="mr-2 inline" />
            Solicitações
            {row.pendingCount > 0 ? ` (${row.pendingCount})` : ''}
          </Dropdown.Item>
          <Dropdown.Item id="usages" textValue="Histórico">
            <History size={16} className="mr-2 inline" />
            Histórico de usos
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  );
}

export function OwnedSpotsSection({
  rows,
  onAvailability,
  onActiveWindows,
  onPending,
  onUsages,
  onViewLocation,
  onReclaim,
}: OwnedSpotsSectionProps) {
  if (rows.length === 0) {
    return (
      <EmptyState className="flex flex-col items-center gap-2 py-12 text-center">
        <Inbox size={40} className="text-muted" />
        <Heading level={3}>Nenhuma vaga associada</Heading>
        <Description>
          Peça ao administrador do condomínio para vincular uma vaga à sua conta.
        </Description>
      </EmptyState>
    );
  }

  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <Table aria-label="Minhas vagas">
          <Table.ScrollContainer>
            <Table.Content>
              <Table.Header>
                <Table.Column isRowHeader>Vaga</Table.Column>
                <Table.Column>Local</Table.Column>
                <Table.Column>Status</Table.Column>
                <Table.Column>Solicitações</Table.Column>
                <Table.Column className="text-right">Ações</Table.Column>
              </Table.Header>
              <Table.Body items={rows}>
                {(row) => (
                  <Table.Row id={row.spot.id}>
                    <Table.Cell>
                      <span className="font-mono text-lg font-bold tracking-tight">
                        {row.spot.spotCode}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="text-muted">
                      {[row.spot.spotLevel, row.spot.spotLocation].filter(Boolean).join(' · ') ||
                        '—'}
                    </Table.Cell>
                    <Table.Cell>
                      <SpotStatusChip status={row.status} />
                    </Table.Cell>
                    <Table.Cell>
                      {row.pendingCount > 0 ? (
                        <Chip size="sm" variant="soft" color="danger">
                          {row.pendingCount}
                        </Chip>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </Table.Cell>
                    <Table.Cell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {row.activeUsage ? (
                          <Button
                            size="sm"
                            variant="danger"
                            onPress={() => onReclaim(row.activeUsage!.id)}
                          >
                            <RotateCcw size={14} />
                            Retomar vaga
                          </Button>
                        ) : null}
                        <SpotActionsMenu
                          row={row}
                          onAvailability={onAvailability}
                          onActiveWindows={onActiveWindows}
                          onPending={onPending}
                          onUsages={onUsages}
                          onViewLocation={onViewLocation}
                          onReclaim={onReclaim}
                        />
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </div>

      <div className="flex flex-col gap-3 md:hidden">
        {rows.map((row) => (
          <Card key={row.spot.id}>
            <Card.Header className="flex-row items-start justify-between gap-3 pb-0">
              <div className="min-w-0">
                <Card.Title className="font-mono text-lg font-bold tracking-tight">
                  {row.spot.spotCode}
                </Card.Title>
                <Card.Description>
                  {[row.spot.spotLevel, row.spot.spotLocation].filter(Boolean).join(' · ') ||
                    'Sem localização'}
                </Card.Description>
              </div>
              <SpotStatusChip status={row.status} />
            </Card.Header>
            <Card.Content className="flex items-center justify-between gap-2 pt-3">
              {row.pendingCount > 0 ? (
                <Chip size="sm" variant="soft" color="danger">
                  {row.pendingCount} pendente(s)
                </Chip>
              ) : (
                <span className="text-sm text-muted">Sem solicitações</span>
              )}
              <SpotActionsMenu
                row={row}
                onAvailability={onAvailability}
                onActiveWindows={onActiveWindows}
                onPending={onPending}
                onUsages={onUsages}
                onViewLocation={onViewLocation}
                onReclaim={onReclaim}
              />
            </Card.Content>
            <Card.Footer className="flex flex-col gap-2 pt-0">
              {row.activeUsage ? (
                <Button
                  variant="danger"
                  className="w-full"
                  onPress={() => onReclaim(row.activeUsage!.id)}
                >
                  <RotateCcw size={16} />
                  Retomar vaga
                </Button>
              ) : null}
              <div className="flex w-full gap-2">
                <Button
                  variant="primary"
                  className="flex-1"
                  onPress={() => onAvailability(row.spot)}
                  data-tour="open-availability-drawer"
                >
                  <CalendarPlus size={16} />
                  Disponibilizar
                </Button>
                {row.activeWindowsCount > 0 ? (
                  <Button
                    variant="secondary"
                    className="flex-1"
                    onPress={() => onActiveWindows(row.spot)}
                    data-tour="active-windows"
                  >
                    <CalendarCheck size={16} />
                    Janelas ativas
                  </Button>
                ) : null}
              </div>
              <div className="flex w-full gap-2">
                {hasFloorPlanLocation(row.spot) ? (
                  <Button
                    variant="secondary"
                    className="flex-1"
                    onPress={() => onViewLocation(row.spot)}
                  >
                    <MapPin size={16} />
                    Ver no mapa
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  className="flex-1"
                  onPress={() => onPending(row.spot)}
                >
                  <Users size={16} />
                  Solicitações
                  {row.pendingCount > 0 ? ` (${row.pendingCount})` : ''}
                </Button>
              </div>
            </Card.Footer>
          </Card>
        ))}
      </div>
    </>
  );
}
